import { v4 } from 'uuid';
import differenceInDays from 'date-fns/differenceInDays';
import {
  IHotelsSearchRequest,
  IHotelsSearchFiltersRequest,
  IHotelsRequest,
} from '@common-types';
import {
  DateSearchTypeEnum,
  IRoomResponse,
  IUrlRoom,
} from '@share/common-types';

import {
  ILocation,
  IRoom,
} from '@share/common-types';
import { DEFAULT_PAGE_SIZE, DEFAULT_RESIDENCY } from '@share/constants';
import { IHotelsState, IFiltersState, ICondosLocationPickerState, ICondoStrictDatesState, ICarLocationsState, ICarsDatesState, ICarsFiltersState, ICarsDriverState } from '@share/store/slices';
import { IDatesState, ILocationsState } from '@share/store/slices';
import { formatDateCheckInOut } from './dates';
import { get, isEmpty } from 'lodash';

export const CarsMaxDaysRange = 40;

const defaultKidsAge = 0;
const maxDaysRange = 30;
const isoFormat = 'yyyy-MM-dd';
const startTime = 'T00:00:01.000Z';

export const isDataValid = (
  locationsStore: ILocationsState,
  datesStore: IDatesState,
): {
  isDatesValid: boolean;
  isLocationValid: boolean;
  isValid: boolean;
  isDatesMaxError: boolean;
} => {
  const res = {
    isDatesValid: true,
    isLocationValid: true,
    isValid: true,
    isDatesMaxError: false,
  };

  if (!locationsStore.selectedLocation) {
    res.isLocationValid = false;
    res.isValid = false;
  }

  if (!datesStore.endDateSelected || !datesStore.startDateSelected) {
    res.isDatesValid = false;
    res.isValid = false;
  }

  if (datesStore.endDateSelected && datesStore.startDateSelected) {
    const start = new Date(datesStore.startDateSelected);
    const end = new Date(datesStore.endDateSelected);

    if (differenceInDays(end, start) > maxDaysRange) {
      res.isDatesValid = false;
      res.isValid = false;
      res.isDatesMaxError = true;
    }
  }

  return res;
};

export const isCondoDataValid = (
  locationsStore: ICondosLocationPickerState,
  datesStore: ICondoStrictDatesState,
): {
  isDatesValid: boolean;
  isLocationValid: boolean;
  isValid: boolean;
  isDatesMaxError: boolean;
} => {
  const res = {
    isDatesValid: true,
    isLocationValid: true,
    isValid: true,
    isDatesMaxError: false,
  };

  if (!locationsStore.selectedLocation) {
    res.isLocationValid = false;
    res.isValid = false;
  }

  if (datesStore.searchType !== DateSearchTypeEnum.Flexible) {
    if (!datesStore.endDate || !datesStore.startDate) {
      res.isDatesValid = false;
      res.isValid = false;
    }
  
    if (datesStore.endDate && datesStore.startDate) {
      const start = new Date(datesStore.startDate);
      const end = new Date(datesStore.endDate);
  
      if (differenceInDays(end, start) > maxDaysRange) {
        res.isDatesValid = false;
        res.isValid = false;
        res.isDatesMaxError = true;
      }
    }
  }

  return res;
};

export const isCarsDataValid = (carsLocationsStore: ICarLocationsState, carsDatesStore: ICarsDatesState, carsDriverStore: ICarsDriverState): {
  isDatesValid: boolean;
  isLocationValid: boolean;
  isValid: boolean;
  isDatesMaxError: boolean;
  isTimesError: boolean;
  isDriverCountryError: boolean;
} => {
  const res = {
    isDatesValid: true,
    isLocationValid: true,
    isValid: true,
    isDatesMaxError: false,
    isTimesError: false,
    isDriverCountryError: false,
  };

  if (!carsLocationsStore.pickUp.selectedLocation) {
    res.isLocationValid = false;
    res.isValid = false;
  }

  if (!carsDatesStore.endDateSelected || !carsDatesStore.startDateSelected) {
    res.isDatesValid = false;
    res.isValid = false;
  }

  if (!carsDatesStore.endDateTimeSelected || !carsDatesStore.startDateTimeSelected) {
    res.isDatesValid = false;
    res.isTimesError = true;
    res.isValid = false;
  }

  if (carsDatesStore.endDateSelected && carsDatesStore.startDateSelected) {
    const start = new Date(carsDatesStore.startDateSelected);
    const end = new Date(carsDatesStore.endDateSelected);

    if (differenceInDays(end, start) > CarsMaxDaysRange) {
      res.isDatesValid = false;
      res.isValid = false;
      res.isDatesMaxError = true;
    }
  }
  
  if (!carsDriverStore.driverCountrySelected) {
    res.isDriverCountryError = true;
    res.isValid = false;
  }

  return res;
};

export const getFilters = (filtersStore: IFiltersState): IHotelsSearchFiltersRequest => {
  const {
    budget,
    selectedAutocompleteItem,
    accommodationType,
    boardNames,
    refundable,
    amenities,
    distance,
    starRatings,
    neighbourhoods,
    flexibleDates,
    accessibility,
    popularLocations
  } = filtersStore;

  const filters = {
    budgetRanges: budget.map((range: string) => {
      const [from, to]: string[] = range.split('-');

      return { from: +from || null, to: +to || null };
    }),
    propertyName: selectedAutocompleteItem ? selectedAutocompleteItem.hotelName : '',
    accommodationType: accommodationType,
    boardNames: boardNames,
    refundable: refundable,
    amenities: amenities,
    starRatings: starRatings,
    distanceInMiles: distance,
    neighbourhoods: neighbourhoods,
    accessibilities: accessibility,
    popularLocations: popularLocations
  } as IHotelsSearchFiltersRequest;

  const flexibleDate = get(flexibleDates, '[0]');
  filters.exactSearch = !flexibleDate || flexibleDate === 'true';

  return filters;
};

export const getNormalizedRooms = (rooms: IUrlRoom[] = []): IRoom[] => {
  return rooms.map((value: IUrlRoom) => {
    return {
      id: v4(),
      adultsCount: value.adultsCount,
      kids: value.kids.map((age: number) => ({ id: v4(), age })),
    };
  });
};

export const getNormalizedRoomsResponse = (rooms: IRoomResponse[] = []): IRoom[] => {
  return rooms.map((value: IRoomResponse) => {
    return {
      id: v4(),
      adultsCount: value.adultsCount ? value.adultsCount : 2,
      kids: value?.kidsAges?.map((age: number) => ({ id: v4(), age })) || [],
    };
  });
};

export const getResponseRooms = (rooms: IRoom[]): IRoomResponse[] => {
  return rooms.map(({ adultsCount, kids }) => {
    return { adultsCount, kidsAges: kids?.map(({ age }) => age || defaultKidsAge) };
  });
};

export const getHotelsRequest = (
  rooms: IRoom[],
  startDate: string,
  endDate: string,
  location: ILocation
): IHotelsRequest => {
  return {
    residency: DEFAULT_RESIDENCY,
    locationCode: location?.code,
    location: location,
    checkIn: formatDateCheckInOut(startDate, isoFormat) + startTime,
    checkOut: formatDateCheckInOut(endDate, isoFormat) + startTime,
    rooms: getResponseRooms(rooms),
  };
};

export const getSearchBody = (
  location: ILocation,
  startDate: string,
  endDate: string,
  rooms: IRoom[],
  hotelsStore: IHotelsState,
  filtersStore: IFiltersState,
  isLoadMore: boolean,
  isReuseSessionKey: boolean,
  hotelId: number = null,
  quick = false,
  currency: string,
  lifeStyle: string
): IHotelsSearchRequest => {
  const { sortBy, searchHomes } = filtersStore;
  const filter = getFilters(filtersStore);

  const req: IHotelsSearchRequest = {
    hotelsRequest: getHotelsRequest(rooms, startDate, endDate, location),
    filter: filter,
    sortBy: sortBy,
    pageNumber: hotelsStore.pageNumber,
    pageSize: DEFAULT_PAGE_SIZE,
    quick: quick,
    searchHomes: searchHomes,
    currency: currency
  };

  if (hotelId) {
    req.hotelId = hotelId;
  }

  if (!isEmpty(lifeStyle)) {
    req.lifeStyle = lifeStyle;
  }

  const type: string = location?.type;
  if (type === 'Related') {
    if (hotelsStore.similarSessionKey && (isLoadMore || isReuseSessionKey)) {
      req.sessionKey = { ...hotelsStore.similarSessionKey };
    } else if (hotelsStore.sessionKey && (isLoadMore || isReuseSessionKey)) {
      req.sessionKey = { ...hotelsStore.sessionKey };
    }
  } else if (hotelsStore.sessionKey && (isLoadMore || isReuseSessionKey)) {
    req.sessionKey = { ...hotelsStore.sessionKey };
  }

  return req;
};

export const GetCarsFilters = (filtersStore: ICarsFiltersState) => {
  const {
    airCondition,
    budget,
    carType,
    category,
    doors,
    driveType,
    fuel,
    rateDistance,
    rentalCompany,
    seats,
    transmissionType,
    refundable,
    whereLocation,
    simpleType
  } = filtersStore;

  const filters: any = {};

  let emptyFilters = true;

  if (budget?.length) {
    filters.budgetRanges = budget.map((range: string) => {
      const [from, to]: string[] = range.split('-');
      return { from: +from || null, to: +to || null };
    });
    emptyFilters = false;
  }
  if (airCondition?.length) {
    filters.airCondition = airCondition;
    emptyFilters = false;
  }
  if (carType?.length) {
    filters.carType = carType;
    emptyFilters = false;
  }
  if (category?.length) {
    filters.category = category;
    emptyFilters = false;
  }
  if (doors?.length) {
    filters.doors = doors;
    emptyFilters = false;
  }
  if (driveType?.length) {
    filters.driveType = driveType;
    emptyFilters = false;
  }
  if (fuel?.length) {
    filters.fuel = fuel;
    emptyFilters = false;
  }
  if (rateDistance?.length) {
    filters.rateDistance = rateDistance;
    emptyFilters = false;
  }
  if (simpleType?.length) {
    filters.simpleType = simpleType;
    emptyFilters = false;
  }
  if (rentalCompany?.length) {
    filters.rentalCompany = rentalCompany;
    emptyFilters = false;
  }
  if (seats?.length) {
    filters.seats = seats;
    emptyFilters = false;
  }
  if (transmissionType?.length) {
    filters.transmissionType = transmissionType;
    emptyFilters = false;
  }
  if (refundable?.length) {
    filters.refundable = refundable;
    emptyFilters = false;
  }
  if (whereLocation?.length) {
    filters.whereLocation = whereLocation;
    emptyFilters = false;
  }
  
  if (emptyFilters) {
    return undefined;
  }

  return filters;
};
